/**
 * @fileoverview AssignmentForm Molecule Component
 * @author MCHD Room Scheduler Team
 * @created 2025-10-08 23:55:00
 * 
 * Molecule component for assignment form with hierarchical selection and validation.
 * Follows atomic design principles and hospital-grade validation requirements.
 */

import React from 'react';
import {
  Stack,
  Text,
  Select,
  Textarea,
  Group,
  Box,
  Badge,
  Divider,
  ActionIcon,
  Tooltip
} from '@mantine/core';
import {
  IconMapPin,
  IconClock,
  IconInfoCircle
} from '@tabler/icons-react';
import { ModernDatePicker } from '../../../../components/molecules/ModernDatePicker';
import { startOfDay } from 'date-fns';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

interface Doctor {
  id: string;
  name: string;
  email: string;
  specialty?: string;
  department?: string;
  role?: string;
}

interface SessionTime {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
}

interface Room {
  id: string;
  number: string;
  name: string;
  capacity: number;
  isActive: boolean;
}

interface Assignment {
  id: string;
  title: string;
  description?: string;
  roomId: string;
  userId: string;
  sessionTemplateId: string;
  status: 'PENDING' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED';
  date: string;
  room: Room;
  user?: Doctor;
  users?: Doctor;
  doctor?: Doctor;
  sessionTime?: SessionTime;
  session_times?: SessionTime;
}

interface AssignmentFormProps {
  // Hierarchical selection props
  selectedBuildingId: string;
  selectedFloorId: string;
  selectedRoomId: string;
  selectedSessionTimeId: string;
  selectedDate: Date | null;
  note: string;
  selectedDoctorId: string;
  
  // Data props
  room?: Room | undefined;
  sessionTime?: SessionTime | undefined;
  date?: Date | undefined;
  filterOptions?: {
    buildings: Array<{ value: string; label: string }>;
    floors: Array<{ value: string; label: string }>;
    rooms: Array<{ value: string; label: string }>;
    sessionTimes: Array<{ value: string; label: string }>;
    users: Array<{ value: string; label: string }>;
  } | undefined;
  // ENHANCED: 09-10-2025 01:45:00 - Edit mode support
  selectedAssignment?: Assignment | null;
  
  // Change handlers
  onBuildingChange: (value: string) => void;
  onFloorChange: (value: string) => void;
  onRoomChange: (value: string) => void;
  onSessionTimeChange: (value: string) => void;
  onDateChange: (date: Date | null) => void;
  onNoteChange: (note: string) => void;
  onDoctorChange: (doctorId: string) => void;
  
  // Filtered data
  availableFloors: Array<{ value: string; label: string }>;
  availableRooms: Array<{ value: string; label: string }>;
  availableSessionTimes: Array<{ value: string; label: string }>;
  filteredDoctors: Doctor[];
  
  // UI state
  loading?: boolean;
  submitting?: boolean;
}

/**
 * AssignmentForm Molecule Component
 * 
 * Handles hierarchical selection (Building → Floor → Room → Session Time)
 * and form input management for assignment creation.
 * 
 * Features:
 * - Hierarchical building/floor/room/session selection
 * - Timezone-aware date selection
 * - Doctor selection with search
 * - Optional note field
 * - Production-ready validation
 * 
 * @param props - AssignmentForm component props
 * @returns AssignmentForm molecule component
 */
export const AssignmentForm: React.FC<AssignmentFormProps> = ({
  selectedBuildingId,
  selectedFloorId,
  selectedRoomId,
  selectedSessionTimeId,
  selectedDate,
  note,
  selectedDoctorId,
  room,
  sessionTime,
  filterOptions,
  selectedAssignment,
  onBuildingChange,
  onFloorChange,
  onRoomChange,
  onSessionTimeChange,
  onDateChange,
  onNoteChange,
  onDoctorChange,
  availableFloors,
  availableRooms,
  availableSessionTimes,
  filteredDoctors,
  loading = false,
  submitting = false
}) => {
  return (
    <Stack gap="md">
      {/* Assignment Details */}
      <Box>
        <Text size="sm" fw={500} mb="md" c="dimmed">ASSIGNMENT DETAILS</Text>
        <Stack gap="md">
          {/* Hierarchical Selection */}
          {!room && (
            <>
              {/* Building Selection */}
              <Box>
                <Text size="sm" fw={500} mb="xs">Building</Text>
                <Select
                  placeholder="Select building..."
                  data={filterOptions?.buildings || []}
                  value={selectedBuildingId}
                  onChange={(value) => onBuildingChange(value || '')}
                  searchable
                  required
                  size="sm"
                  disabled={loading || submitting}
                />
              </Box>
              
              {/* Floor Selection */}
              {selectedBuildingId && (
                <Box>
                  <Text size="sm" fw={500} mb="xs">Floor</Text>
                  <Select
                    placeholder="Select floor..."
                    data={availableFloors}
                    value={selectedFloorId}
                    onChange={(value) => onFloorChange(value || '')}
                    searchable
                    required
                    size="sm"
                    disabled={loading || submitting}
                  />
                </Box>
              )}
              
              {/* Room Selection */}
              {selectedFloorId && (
                <Box>
                  <Text size="sm" fw={500} mb="xs">Room</Text>
                  <Select
                    placeholder="Select room..."
                    data={availableRooms}
                    value={selectedRoomId}
                    onChange={(value) => onRoomChange(value || '')}
                    searchable
                    required
                    size="sm"
                    disabled={loading || submitting}
                  />
                </Box>
              )}
            </>
          )}
          
          {/* Room Info Display (when room is pre-selected) */}
          {room && (
            <Group gap="sm" wrap="nowrap">
              <IconMapPin size={16} color="var(--mantine-color-blue-6)" />
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={500} lineClamp={1}>
                  Room: {room.number} - {room.name}
                </Text>
                <Badge size="xs" variant="light" color="blue" mt={4}>
                  Capacity: {room.capacity}
                </Badge>
              </Box>
            </Group>
          )}
          
          {/* Session Time Selection */}
          {!sessionTime && selectedRoomId && (
            <Box>
              <Text size="sm" fw={500} mb="xs">Session Time</Text>
              <Select
                placeholder="Select session time..."
                data={availableSessionTimes}
                value={selectedSessionTimeId}
                onChange={(value) => onSessionTimeChange(value || '')}
                searchable
                required
                size="sm"
                disabled={loading || submitting || !!selectedAssignment}
                description={selectedAssignment ? "Session time cannot be changed when editing existing assignment" : undefined}
              />
            </Box>
          )}
          
          {/* Session Info Display (when session is pre-selected) */}
          {sessionTime && (
            <Group gap="sm" wrap="nowrap">
              <IconClock size={16} color="var(--mantine-color-green-6)" />
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={500} lineClamp={1}>
                  Session: {sessionTime.name}
                </Text>
                <Text size="xs" c="dimmed">
                  {sessionTime.startTime} - {sessionTime.endTime}
                </Text>
              </Box>
            </Group>
          )}
          
          {/* Date Selection */}
          <ModernDatePicker
            label="Assignment Date"
            value={selectedDate}
            onChange={onDateChange}
            placeholder="Select assignment date"
            minDate={startOfDay(new Date())}
            required
            size="sm"
          />
        </Stack>
      </Box>

      <Divider />

      {/* Doctor Selection */}
      <Box>
        <Group gap="xs" mb="md">
          <Text size="sm" fw={500}>Select Doctor *</Text>
          <Tooltip label="Choose a doctor to assign to this room and session time. You can search by name, email, or specialty.">
            <ActionIcon size="xs" variant="subtle" color="gray" aria-label="Doctor selection help">
              <IconInfoCircle size={12} />
            </ActionIcon>
          </Tooltip>
        </Group>
        
        <Select
          placeholder={loading ? "Loading doctors..." : "Search and select a doctor..."}
          data={(() => {
            try {
              const safeDoctors = filteredDoctors || [];
              
              // Group doctors by specialty for proper Mantine Select grouped data structure
              const groupedBySpecialty = safeDoctors.reduce((acc, doctor) => {
                const specialty = doctor.specialty || 'General';
                if (!acc[specialty]) {
                  acc[specialty] = [];
                }
                acc[specialty].push({
                  value: doctor.id,
                  label: doctor.name,
                  description: `ID: ${doctor.id} • Role: ${doctor.role}`
                });
                return acc;
              }, {} as Record<string, Array<{ value: string; label: string; description: string }>>);
              
              // Convert to Mantine's expected grouped data structure
              const mantineGroupedData = Object.entries(groupedBySpecialty).map(([specialty, items]) => ({
                group: specialty,
                items: items
              }));
              
              return mantineGroupedData;
            } catch (error) {
              // Silent error handling for production
              return [];
            }
          })()}
          value={selectedDoctorId}
          onChange={(value) => value && onDoctorChange(value)}
          searchable
          disabled={loading || !filteredDoctors || filteredDoctors.length === 0 || submitting}
          required
          clearable
          styles={{
            dropdown: { 
              maxHeight: 300,
              zIndex: 1000
            },
            option: {
              padding: '8px 12px',
              fontSize: '14px'
            },
            description: {
              fontSize: '12px',
              color: 'var(--mantine-color-dimmed)',
              marginTop: '2px'
            }
          }}
          size="sm"
        />
        
        {/* Doctor Info */}
        {selectedDoctorId && (
          <Box mt="sm" p="sm" bg="blue.0" style={{ borderRadius: '8px', border: '1px solid var(--mantine-color-blue-2)' }}>
            <Group gap="xs">
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={500} lineClamp={1}>
                  {(filteredDoctors || []).find(d => d.id === selectedDoctorId)?.name || 'Unknown Doctor'}
                </Text>
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {(filteredDoctors || []).find(d => d.id === selectedDoctorId)?.email || ''}
                </Text>
              </Box>
            </Group>
          </Box>
        )}
      </Box>

      {/* Note Field */}
      <Box>
        <Group gap="xs" mb="md">
          <Text size="sm" fw={500}>Additional Note (Optional)</Text>
          <Tooltip label="Add any additional information about this assignment that might be helpful for the doctor or other staff.">
            <ActionIcon size="xs" variant="subtle" color="gray" aria-label="Note field help">
              <IconInfoCircle size={12} />
            </ActionIcon>
          </Tooltip>
        </Group>
        <Textarea
          placeholder="Add any additional notes for this assignment..."
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          minRows={3}
          maxRows={5}
          disabled={loading || submitting}
          styles={{
            input: {
              fontSize: '14px',
              lineHeight: '1.4'
            }
          }}
        />
      </Box>
    </Stack>
  );
};

export default React.memo(AssignmentForm);
